'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Select, { components } from 'react-select'; // Make sure to import components
import { Plus, Save, X, Clock, DollarSign, FlaskConical } from 'lucide-react';
import { Container, Row, Card, Input, Label } from "reactstrap";
import { Col } from "react-bootstrap";
import { Breadcrumbs } from '../../AbstractElements';
import { Link, useNavigate } from 'react-router-dom';
import { useLoader } from '../../_helper/LoaderContext';
import { addTest, globalTest, importGlobalTests, masterDepartment, masterParameter, masterSample, masterTest } from './service/test-service';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';

// Custom Checkbox Option Component
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};

const allOption = { label: "Select All", value: "*" };

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '38px',
        borderRadius: '0.375rem',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '2px 8px',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected || state.isFocused ? '' : '',
        color: '#333',
        ':active': {
            backgroundColor: '',
        },
    }),
};

const multiSelectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '38px',
        height: '38px',
        overflow: 'hidden',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '38px',
        padding: '0 8px',
        overflowX: 'auto',
        overflowY: 'hidden',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#1b255b',
        margin: '2px',
        flexShrink: 0,
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#fff',
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#d32f2f',
        ':hover': {
            backgroundColor: '#ffcdd2',
            color: 'white',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }),
};

export default function AddTest() {
    const { t } = useTranslation();
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        trigger,
        watch
    } = useForm({
        mode: 'onTouched',
    });

    const { setLoading } = useLoader();
    const navigate = useNavigate();

    const [departmentOption, setDepartment] = useState([]);
    const [sample, setSample] = useState([]);
    const [testMethods, setTestMethods] = useState([]);
    const [parameterOptions, setParameterOptions] = useState([]);
    const [globalTestMaster, setGlobalTestMaster] = useState([]);
    const [selectedGlobalTests, setSelectedGlobalTests] = useState([]);
    const [imported, setImported] = useState("add");
    const [parameterGroups, setParameterGroups] = useState([
        { id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }
    ]);

    const [userData, setUserData] = useState({});

    useEffect(() => {
        getDepartmentMaster();
        getSampleMaster();
        getTestMaster();
        getParameterMaster();
        const data = JSON.parse(localStorage.getItem("currentLab"));
        setUserData(data);
        getGlobalTest();

        register('department', { required: t('Please select any department') });
        register('sampleType', { required: t('Please select any sample type') });
        register('testMethod', { required: t('Please select any test method') });
    }, [register, t]);

    const getGlobalTest = async () => {
        setLoading(true);
        try {
            const res = await globalTest();
            if (res.data.success == true) {
                setGlobalTestMaster(res.data.data || []);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const globalTestOptions = useMemo(() => {
        const options = (globalTestMaster || []).map((item) => ({
            value: item._id || item.id,
            label: item.code ? `${item.name} (${item.code})` : item.name,
            data: item,
        }));
        return options.length > 0 ? [{ label: t("Select All"), value: "*" }, ...options] : [];
    }, [globalTestMaster, t]);

    const handleGlobalTestChange = (selected) => {
        if (!selected) {
            setSelectedGlobalTests([]);
            return;
        }

        const hasSelectAll = selected.some((opt) => opt.value === "*");
        if (hasSelectAll) {
            if (selectedGlobalTests.length === globalTestMaster.length) {
                setSelectedGlobalTests([]);
            } else {
                setSelectedGlobalTests(
                    globalTestMaster.map((item) => ({
                        value: item._id || item.id,
                        label: item.code ? `${item.name} (${item.code})` : item.name,
                        data: item,
                    }))
                );
            }
        } else {
            setSelectedGlobalTests(selected.filter((opt) => opt.value !== "*"));
        }
    };

    const handleImportGlobalTests = async () => {
        const masterTestIds = selectedGlobalTests
            .filter((item) => item.value !== "*")
            .map((item) => item.value);

        if (masterTestIds.length === 0) {
            toast.info(t("No tests selected"));
            return;
        }

        setLoading(true);
        try {
            const res = await importGlobalTests({ masterTestIds });
            if (res.data.success === true) {
                const summary = res.data.data;
                const importedCount = summary?.importedCount;
                toast.success(
                    importedCount !== undefined
                        ? `${res.data.message || t("Tests imported successfully")} (${importedCount} imported)`
                        : res.data.message || t("Tests imported successfully")
                );
                setSelectedGlobalTests([]);
                navigate(`${process.env.PUBLIC_URL}/list-test`);
            } else {
                toast.warning(res.data.message || t("Unable to import tests"));
            }
        } catch (error) {
            console.log(error);
            toast.warning(error?.response?.data?.message || t("Unable to import tests"));
        } finally {
            setLoading(false);
        }
    };

    const getDepartmentMaster = async () => {
        setLoading(true);
        try {
            const res = await masterDepartment();
            if (res.data.success) setDepartment(res.data.data.data);
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const getSampleMaster = async () => {
        setLoading(true);
        try {
            const res = await masterSample();
            if (res.data.success) setSample(res.data.data);
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const getTestMaster = async () => {
        setLoading(true);
        try {
            const res = await masterTest();
            if (res.data.success) setTestMethods(res.data.data);
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const getParameterMaster = async () => {
        setLoading(true);
        try {
            const res = await masterParameter();
            if (res.data.success) {
                const formatted = res.data.data.map(item => ({
                    value: item._id,
                    label: item.name,
                    fullData: item
                }));
                setParameterOptions(formatted);
            }
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const addParameterGroup = () => {
        setParameterGroups([
            ...parameterGroups,
            { id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }
        ]);
    };

    const removeParameterGroup = (id) => {
        if (parameterGroups.length === 1) {
            toast.warn(t("At least one parameter group is required"));
            return;
        }
        setParameterGroups(parameterGroups.filter(g => g.id !== id));
    };

    const updateGroupName = (id, value) => {
        setParameterGroups(prev =>
            prev.map(g =>
                g.id === id
                    ? { ...g, groupName: value, errors: { ...g.errors, groupName: '' } }
                    : g
            )
        );
    };

    const updateGroupParameters = (id, selected) => {
        setParameterGroups(prev => prev.map(g => {
            if (g.id === id) {
                const error = (selected && selected.length > 0) ? '' : t('Select at least one parameter');
                return { ...g, parameters: selected || [], errors: { ...g.errors, parameters: error } };
            }
            return g;
        }));
    };

    const validateAllGroups = () => {
        let valid = true;

        setParameterGroups(prev =>
            prev.map(g => {
                const paramError = g.parameters.length > 0 ? '' : t('Select at least one parameter');
                if (paramError) valid = false;

                return {
                    ...g,
                    errors: {
                        ...g.errors,
                        parameters: paramError,
                        groupName: ''
                    }
                };
            })
        );

        return valid;
    };

    const onSubmit = async (data) => {
        if (!validateAllGroups()) {
            toast.warning(t("Please fix parameter group errors"));
            return;
        }
        if (parameterGroups.some(g => g.parameters.length === 0)) {
            toast.warning(t("Each group must have at least one parameter"));
            return;
        }
        setLoading(true);
        try {
            const payload = {
                name: data.testName,
                code: data.testCode,
                departmentId: data.department,
                departmentName: departmentOption.find(d => d._id === data.department)?.name || "",
                sampleTypeId: data.sampleType,
                sampleTypeName: sample.find(s => s._id === data.sampleType)?.name || "",
                samples: (() => {
                    const selectedSample = sample.find(s => s._id === data.sampleType);
                    if (!selectedSample) return [];
                    const assignedEquipment = (selectedSample.requiredEquipment || [])
                        .map((eq) => (typeof eq === "string" ? eq : eq?._id))
                        .filter(Boolean);
                    return [{
                        sampleId: selectedSample._id,
                        name: selectedSample.name || "",
                        description: selectedSample.description || "",
                        assignedEquipment,
                    }];
                })(),
                methodId: data.testMethod,
                methodName: testMethods.find(t => t._id === data.testMethod)?.name || "",
                labId: userData.labId,
                cost: Number(data.price),
                turnaroundTime: {
                    value: Number(data.tatValue),
                    unit: data.tatUnit || "hours"
                },
                interpretation: data.interpretation || "",
                parameterGroups: parameterGroups.map(group => ({
                    groupName: group.groupName.trim(),
                    parameters: group.parameters.map(p => ({
                        parameterId: p.value,
                        name: p.label,
                        code: p.fullData.code || "",
                        unitValue: p.fullData.unitValue || "",
                        type: p.fullData.type || "quantitative",
                        referenceRanges: p.fullData.referenceRanges && p.fullData.referenceRanges.length > 0
                            ? p.fullData.referenceRanges
                            : [
                                {
                                    gender: "all",
                                    ageCategory: "adult",
                                    minValue: 0,
                                    maxValue: 0
                                }
                            ]
                    }))
                }))
            };

            const res = await addTest(payload);
            if (res.data.success) {
                toast.success(t("Test added successfully!"));
                reset();
                navigate(`${process.env.PUBLIC_URL}/list-test`);
            }
        } catch (error) {
            console.log(error.response?.data?.message);
            // toast.warning(error.response?.data?.message || "Failed to save test");
        } finally {
            setLoading(false);
        }
    };

    const handleRadio = (e) => {
        const value = e.target.value;
        setImported(value);

        if (value === "add") {
            setSelectedGlobalTests([]);
            reset();
            setParameterGroups([
                { id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }
            ]);
        } else {
            setSelectedGlobalTests([]);
        }
    };

    const departmentSelectOptions = departmentOption.map(d => ({
        value: d._id,
        label: d.name
    }));

    const sampleSelectOptions = sample.map(s => ({
        value: s._id,
        label: s.name
    }));

    const methodSelectOptions = testMethods.map(t => ({
        value: t._id,
        label: t.name
    }));

    const getAvailableParametersForGroup = (currentGroupId) => {
        // Get all selected parameter values from OTHER groups
        const selectedInOtherGroups = parameterGroups
            .filter(g => g.id !== currentGroupId)
            .flatMap(g => g.parameters.map(p => p.value));

        // Filter out those from the master list
        return parameterOptions.filter(option =>
            !selectedInOtherGroups.includes(option.value)
        );
    };

    return (
        <>
            <Breadcrumbs mainTitle={t("Add Test")} parent={t("Home")} subParent={t("List of Lab Tests")} title={t("Add Test")} />
            <Container fluid={true}>
                <Row>
                    <Col>
                        <Card>
                            <div className="card-body p-3 pt-3">
                                <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                                    <div className="radio radio-primary">
                                        <Input
                                            id="test-radio-add"
                                            type="radio"
                                            name="testImportType"
                                            value="add"
                                            checked={imported === "add"}
                                            onChange={handleRadio}
                                        />
                                        <Label for="test-radio-add" className="mb-0 pe-2">
                                            {t("Manual")}
                                        </Label>
                                    </div>
                                    <div className="radio radio-primary">
                                        <Input
                                            id="test-radio-global"
                                            type="radio"
                                            name="testImportType"
                                            value="gloabal"
                                            checked={imported === "gloabal"}
                                            onChange={handleRadio}
                                        />
                                        <Label for="test-radio-global" className="mb-0 pe-2">
                                            {t("Import From Database")}
                                        </Label>
                                    </div>
                                    {imported === "gloabal" && selectedGlobalTests.length > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={handleImportGlobalTests}
                                        >
                                            {t("Import")} ({selectedGlobalTests.length})
                                        </button>
                                    )}
                                </div>

                                {imported === "add" ? (
                                    <form onSubmit={handleSubmit(onSubmit)} noValidate>

                                        {/* Row 1 */}
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold mb-1">{t("Test Name")} <span className="text-danger">*</span></label>
                                                <input
                                                    {...register('testName', {
                                                        required: t('Test name is required'),
                                                        minLength: { value: 3, message: t('Minimum 3 characters') }
                                                    })}
                                                    type="text"
                                                    className={`form-control rounded-3 ${errors.testName ? 'is-invalid' : ''}`}
                                                    placeholder={t("e.g. Comprehensive Metabolic Panel")}
                                                />
                                                {errors.testName && <div className="invalid-feedback">{errors.testName.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold mb-1">{t("Test Code")} <span className="text-danger">*</span></label>
                                                <input
                                                    {...register('testCode', {
                                                        required: t('Test code is required'),
                                                    })}
                                                    type="text"
                                                    className={`form-control rounded-3 ${errors.testCode ? 'is-invalid' : ''}`}
                                                    placeholder={t("e.g. CMP001")}
                                                />
                                                {errors.testCode && <div className="invalid-feedback">{errors.testCode.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold mb-1">{t("Department")} <span className="text-danger">*</span>
                                                    <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="name-tooltip"></i>
                                                    <Tooltip id="name-tooltip" place="top" content={t('Clinical department responsible for this test.')} />
                                                </label>

                                                <Select
                                                    options={departmentSelectOptions}
                                                    value={departmentSelectOptions.find(opt => opt.value === watch('department')) || null}
                                                    onChange={(option) => setValue('department', option ? option.value : '', { shouldValidate: true })}
                                                    placeholder={t("Search department...")}
                                                    isSearchable
                                                    isClearable
                                                    filterOption={(option, input) =>
                                                        option.label.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                                {errors.department && <div className="text-danger tw-text-[0.875em] mt-1">{errors.department.message}</div>}
                                            </div>
                                        </div>

                                        {/* Row 2 */}
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold mb-1">{t("Sample Type")} <span className="text-danger">*</span>
                                                    <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="sample-tooltip"></i>
                                                    <Tooltip id="sample-tooltip" place="top" content={t('Type of biological sample required for testing.')} />
                                                </label>

                                                <Select
                                                    options={sampleSelectOptions}
                                                    value={sampleSelectOptions.find(opt => opt.value === watch('sampleType')) || null}
                                                    onChange={(option) => setValue('sampleType', option ? option.value : '', { shouldValidate: true })}
                                                    placeholder={t("Search sample type...")}
                                                    isSearchable
                                                    isClearable
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                                {errors.sampleType && <div className="text-danger tw-text-[0.875em] mt-1">{errors.sampleType.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold mb-1">{t("Test Method")} <span className="text-danger">*</span>
                                                    <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="test-tooltip"></i>
                                                    <Tooltip id="test-tooltip" place="top" content={t('Laboratory technique used to perform this test.')} />
                                                </label>

                                                <Select
                                                    options={methodSelectOptions}
                                                    value={methodSelectOptions.find(opt => opt.value === watch('testMethod')) || null}
                                                    onChange={(option) => setValue('testMethod', option ? option.value : '', { shouldValidate: true })}
                                                    placeholder={t("Search method...")}
                                                    isSearchable
                                                    isClearable
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                                {errors.testMethod && <div className="text-danger tw-text-[0.875em] mt-1">{errors.testMethod.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold mb-1">{t("Price")} <span className="text-danger">*</span></label>
                                                <input
                                                    {...register('price', {
                                                        required: t('Price is required'),
                                                        pattern: { value: /^\d+(\.\d{1,2})?$/, message: t('Valid amount e.g. 1800 or 1800.50') }
                                                    })}
                                                    type="text"
                                                    className={`form-control rounded-3 ${errors.price ? 'is-invalid' : ''}`}
                                                    placeholder={t("e.g. 1800.00")}
                                                />
                                                {errors.price && <div className="invalid-feedback">{errors.price.message}</div>}
                                            </div>
                                        </div>

                                        {/* TAT */}
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold mb-1 d-flex align-items-center gap-2">
                                                    <Clock size={16} className="text-warning flex-shrink-0" /> {t("Turnaround Time (TAT)")} <span className="text-danger">*</span>
                                                </label>
                                                <div className="d-flex">
                                                    <input
                                                        {...register('tatValue', {
                                                            required: t('Enter value'),
                                                            pattern: { value: /^\d+$/, message: t('Only numbers') }
                                                        })}
                                                        type="text"
                                                        className={`form-control rounded-start ${errors.tatValue ? 'is-invalid' : ''}`}
                                                        placeholder="6"
                                                        style={{ borderTopRightRadius: '0px', borderBottomRightRadius: '0px' }}
                                                    />
                                                    <select
                                                        {...register('tatUnit', { required: t('Select unit') })}
                                                        className={`form-select tw-bg-gray-300 rounded-end ${errors.tatUnit ? 'is-invalid' : ''}`}
                                                        style={{ width: '130px', borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}
                                                    >
                                                        <option value="">{t("Unit")}</option>
                                                        <option value="minutes">{t("Minutes")}</option>
                                                        <option value="hours">{t("Hours")}</option>
                                                        <option value="days">{t("Days")}</option>
                                                    </select>
                                                </div>
                                                {errors.tatValue && <div className="invalid-feedback d-block">{errors.tatValue.message}</div>}
                                                {errors.tatUnit && <div className="invalid-feedback d-block">{errors.tatUnit.message}</div>}
                                            </div>

                                            <div className="col-md-8">
                                                <label className="form-label fw-semibold mb-1">{t("Interpretation (Optional)")}</label>
                                                <textarea
                                                    {...register('interpretation')}
                                                    className="form-control"
                                                    rows="1"
                                                    placeholder={t("Add clinical interpretation...")}
                                                />
                                            </div>
                                        </div>

                                        {/* Dynamic Parameter Groups */}
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold d-flex align-items-center gap-2 mb-2">
                                                <FlaskConical size={18} className="text-primary flex-shrink-0" />
                                                <span>{t("Bundles")}</span>
                                            </label>

                                            {parameterGroups.map((group, index) => (
                                                <div key={group.id} className="rounded-3 p-3 mb-3 position-relative" style={{ border: "2px solid #1b255b" }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="fw-semibold text-muted tw-text-xs text-uppercase">
                                                            {t("Bundle")} #{index + 1}
                                                        </span>
                                                        {parameterGroups.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeParameterGroup(group.id)}
                                                                className="btn btn-sm btn-outline-danger py-0.5 px-2 tw-text-xs d-inline-flex align-items-center gap-1"
                                                            >
                                                                <X size={13} /> {t("Remove")}
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="form-label fw-semibold mb-1">{t("Bundle Name")}</label>
                                                            <input
                                                                type="text"
                                                                value={group.groupName}
                                                                onChange={(e) => updateGroupName(group.id, e.target.value)}
                                                                className={`form-control ${group.errors.groupName ? 'is-invalid' : ''}`}
                                                                placeholder={t("e.g. LIVER FUNCTION")}
                                                            />
                                                            {group.errors.groupName && <div className="invalid-feedback">{group.errors.groupName}</div>}
                                                        </div>

                                                        <div className="col-md-8">
                                                            <label className="form-label fw-semibold mb-1">{t("Biomarkers")} <span className="text-danger">*</span></label>
                                                            <Select
                                                                isMulti
                                                                isSearchable
                                                                closeMenuOnSelect={false}
                                                                hideSelectedOptions={false}
                                                                options={getAvailableParametersForGroup(group.id)}  // ← Now filtered!
                                                                value={group.parameters}
                                                                onChange={(selected) => updateGroupParameters(group.id, selected)}
                                                                placeholder={t("Search and select parameters...")}
                                                                classNamePrefix="react-select"
                                                                components={{ Option }}
                                                                styles={customStyles}
                                                            />                            
                                                            {group.errors.parameters && (
                                                                <small className="text-danger d-block mt-1">{group.errors.parameters}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="d-flex justify-content-end mb-3">
                                                <button type="button" onClick={addParameterGroup} className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1">
                                                    <Plus size={15} /> {t("Add Another Bundle")}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Submit Buttons */}
                                        <div className="d-flex justify-content-end gap-2 mt-3">
                                            <button type="submit" className="btn btn-primary d-inline-flex align-items-center gap-1">
                                                <Save size={16} /> {t("Save Test")}
                                            </button>
                                            <Link to={`${process.env.PUBLIC_URL}/list-test`}>
                                                <button type="button" className="btn btn-secondary">{t("Cancel")}</button>
                                            </Link>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="row">
                                        <div className="col-md-12">
                                            <label className="form-label fw-semibold mb-1">{t("Add tests from global list")}</label>
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                options={globalTestOptions}
                                                value={selectedGlobalTests}
                                                onChange={handleGlobalTestChange}
                                                placeholder={t("Add tests from global list...")}
                                                components={{ Option }}
                                                styles={multiSelectStyles}
                                                classNamePrefix="react-select"
                                                isClearable={true}
                                                isSearchable
                                            />
                                            <div className="d-flex justify-content-end gap-2 mt-3">
                                                {selectedGlobalTests.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={handleImportGlobalTests}
                                                    >
                                                        {t("Import")} ({selectedGlobalTests.length})
                                                    </button>
                                                )}
                                                <Link to={`${process.env.PUBLIC_URL}/list-test`}>
                                                    <button type="button" className="btn btn-secondary">{t("Cancel")}</button>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}